import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API_URL } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import AdminNav from './AdminNav';

const TABS = [
  { id: 'conectados',  label: '🟢 Conectados' },
  { id: 'sesiones',    label: '🕐 Sesiones' },
  { id: 'inactividad', label: '😴 Inactividad' },
  { id: 'ips',         label: '🚫 IPs Bloqueadas' },
];

export default function AdminSeguridad() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [esMobile, setEsMobile] = useState(() => window.innerWidth <= 768);
  const [tab, setTab]           = useState('conectados');

  // Conectados ahora
  const [conectados, setConectados]     = useState([]);
  const [cargandoCon, setCargandoCon]   = useState(false);
  const [cerrandoSesion, setCerrandoSesion] = useState(null);

  // Config sesiones simultáneas
  const [sesionesMax, setSesionesMax]   = useState('0');
  const [guardandoCfg, setGuardandoCfg] = useState(false);

  // Sesiones
  const [sesiones, setSesiones]         = useState([]);
  const [cargandoSes, setCargandoSes]   = useState(false);
  const [filtroBusq, setFiltroBusq]     = useState('');

  // Inactividad
  const [dias, setDias]                 = useState(7);
  const [inactivos, setInactivos]       = useState([]);
  const [cargandoInact, setCargandoInact] = useState(false);

  // IPs
  const [ips, setIps]                   = useState([]);
  const [ipForm, setIpForm]             = useState({ ip: '', motivo: '' });
  const [guardandoIp, setGuardandoIp]   = useState(false);
  const [errorIp, setErrorIp]           = useState('');

  useEffect(() => {
    const check = () => setEsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (usuario?.rol !== 'admin') { navigate('/'); return; }
    cargarConectados();
    cargarSesionesConfig();
    cargarSesiones();
    cargarIps();
  }, []);

  // Refresca conectados cada 30s mientras se está en esa pestaña
  useEffect(() => {
    if (tab !== 'conectados') return;
    const interval = setInterval(cargarConectados, 30000);
    return () => clearInterval(interval);
  }, [tab]);

  useEffect(() => {
    if (tab === 'inactividad') cargarInactivos();
  }, [tab]);

  const cargarConectados = async () => {
    setCargandoCon(true);
    try { const r = await axios.get(`${API_URL}/admin/control/usuarios-activos`); setConectados(r.data); }
    catch {} finally { setCargandoCon(false); }
  };

  const cargarSesionesConfig = async () => {
    try { const r = await axios.get(`${API_URL}/admin/control/sesiones-config`); setSesionesMax(r.data.sesiones_max); }
    catch {}
  };

  const guardarSesionesConfig = async (valor) => {
    setGuardandoCfg(true);
    try {
      await axios.put(`${API_URL}/admin/control/sesiones-config`, { sesiones_max: valor });
      setSesionesMax(valor);
    } catch {} finally { setGuardandoCfg(false); }
  };

  const cerrarSesion = async (id, nombre) => {
    if (!window.confirm(`¿Cerrar la sesión de ${nombre}? Será desconectado en su próxima acción.`)) return;
    setCerrandoSesion(id);
    try { await axios.post(`${API_URL}/admin/control/cerrar-sesion/${id}`); cargarConectados(); }
    catch {} finally { setCerrandoSesion(null); }
  };

  const cargarSesiones = async () => {
    setCargandoSes(true);
    try { const r = await axios.get(`${API_URL}/admin/control/sesiones`); setSesiones(r.data); }
    catch {} finally { setCargandoSes(false); }
  };

  const cargarInactivos = useCallback(async () => {
    setCargandoInact(true);
    try { const r = await axios.get(`${API_URL}/admin/control/inactivos?dias=${dias}`); setInactivos(r.data); }
    catch {} finally { setCargandoInact(false); }
  }, [dias]);

  const cargarIps = async () => {
    try { const r = await axios.get(`${API_URL}/admin/control/ips`); setIps(r.data); }
    catch {}
  };

  const bloquearIp = async (e) => {
    e.preventDefault();
    setErrorIp('');
    setGuardandoIp(true);
    try {
      await axios.post(`${API_URL}/admin/control/ips`, ipForm);
      setIpForm({ ip: '', motivo: '' });
      cargarIps();
    } catch (e) {
      setErrorIp(e.response?.data?.error || 'Error al bloquear');
    } finally { setGuardandoIp(false); }
  };

  const desbloquearIp = async (id) => {
    if (!window.confirm('¿Desbloquear esta IP?')) return;
    try { await axios.delete(`${API_URL}/admin/control/ips/${id}`); cargarIps(); }
    catch {}
  };

  const sesionesFiltered = sesiones.filter(s =>
    !filtroBusq ||
    s.nombre?.toLowerCase().includes(filtroBusq.toLowerCase()) ||
    s.email?.toLowerCase().includes(filtroBusq.toLowerCase()) ||
    s.ip?.includes(filtroBusq)
  );

  const fmt = (iso) => iso
    ? new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';

  const tiempoDesde = (iso) => {
    if (!iso) return 'Nunca';
    const seg = Math.floor((Date.now() - new Date(iso)) / 1000);
    if (seg < 60) return 'Hace menos de 1 min';
    if (seg < 3600) return `Hace ${Math.floor(seg / 60)} min`;
    const dias = Math.floor(seg / 86400);
    if (dias === 0) return `Hace ${Math.floor(seg / 3600)}h`;
    if (dias === 1) return 'Ayer';
    return `Hace ${dias} días`;
  };

  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: '10px', background: '#FFF0F5', border: '1px solid #FBCFE8', color: '#581C47', fontSize: '14px' };
  const btnGrad = { background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)', border: 'none', color: '#fff', padding: '10px 20px', borderRadius: '50px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(236,72,153,0.3)' };

  return (
    <div style={{ minHeight: '100vh', background: '#FFF0F5' }}>
      <AdminNav esMobile={esMobile} tipo="subpagina" titulo={esMobile ? 'Seguridad' : 'Seguridad y Actividad'} />

      <div style={{ maxWidth: '1100px', padding: esMobile ? '16px 12px 80px' : '32px 24px' }} className="fade-in">
        <h1 style={{ color: '#581C47', fontSize: esMobile ? '20px' : '26px', marginBottom: '4px' }}>🛡️ Seguridad y Actividad</h1>
        <p style={{ color: '#BE84A3', fontSize: '13px', marginBottom: '20px' }}>Monitorea sesiones, detecta inactividad y controla accesos</p>

        {/* Config sesiones simultáneas — siempre visible */}
        <div style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '14px', padding: '14px 20px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(236,72,153,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <p style={{ color: '#581C47', fontWeight: 600, fontSize: '14px', marginBottom: '2px' }}>Limitar sesiones simultáneas (alumnos)</p>
            <p style={{ color: '#BE84A3', fontSize: '12px' }}>Si está activo, cada nuevo login invalida la sesión anterior</p>
          </div>
          <button
            onClick={() => guardarSesionesConfig(sesionesMax === '1' ? '0' : '1')}
            disabled={guardandoCfg}
            style={{
              background: sesionesMax === '1' ? 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)' : '#F3F4F6',
              border: 'none', color: sesionesMax === '1' ? '#fff' : '#6B7280',
              padding: '8px 20px', borderRadius: '50px', fontSize: '13px', fontWeight: 700,
              cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0,
              boxShadow: sesionesMax === '1' ? '0 4px 12px rgba(236,72,153,0.3)' : 'none',
            }}
          >
            {sesionesMax === '1' ? '● Activo' : '○ Inactivo'}
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '12px', padding: '4px', boxShadow: '0 2px 8px rgba(236,72,153,0.06)', overflowX: 'auto' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: tab === t.id ? 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)' : 'transparent',
              border: 'none', color: tab === t.id ? '#fff' : '#BE84A3',
              padding: esMobile ? '8px 10px' : '8px 18px', borderRadius: '9px', cursor: 'pointer',
              fontSize: esMobile ? '11px' : '13px', fontWeight: 600, transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              boxShadow: tab === t.id ? '0 4px 12px rgba(236,72,153,0.3)' : 'none',
            }}>
              {t.label}
              {t.id === 'conectados' && conectados.length > 0 && (
                <span style={{ background: tab === t.id ? 'rgba(255,255,255,0.3)' : '#EC4899', color: tab === t.id ? '#fff' : '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '10px', fontWeight: 700, marginLeft: '6px' }}>
                  {conectados.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── TAB: CONECTADOS AHORA ─────────────────────────── */}
        {tab === 'conectados' && (
          <>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={cargarConectados} style={{ background: '#FCE7F3', border: '1px solid #FBCFE8', color: '#EC4899', padding: '8px 16px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                ↻ Actualizar
              </button>
              <span style={{ color: '#BE84A3', fontSize: '12px' }}>
                {cargandoCon ? 'Actualizando...' : `${conectados.length} usuario(s) activo(s) en los últimos 5 min · Refresca cada 30s`}
              </span>
            </div>

            {conectados.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px', color: '#BE84A3', background: '#FFFFFF', borderRadius: '14px', border: '1px solid #FBCFE8' }}>
                No hay usuarios conectados ahora mismo
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {conectados.map(u => (
                  <div key={u.id} style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '12px', padding: esMobile ? '14px 16px' : '14px 20px', boxShadow: '0 2px 8px rgba(236,72,153,0.05)', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#34D399', flexShrink: 0, boxShadow: '0 0 6px #34D399' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <p style={{ color: '#581C47', fontWeight: 600, fontSize: '14px' }}>{u.nombre}</p>
                        <span style={{ background: '#FCE7F3', color: '#EC4899', borderRadius: '6px', padding: '1px 8px', fontSize: '10px', fontWeight: 700 }}>{u.rol?.toUpperCase()}</span>
                        {u.forzar_cambio_password ? <span style={{ background: '#FEF3C7', color: '#B45309', borderRadius: '6px', padding: '1px 8px', fontSize: '10px', fontWeight: 700 }}>🔑 Debe cambiar contraseña</span> : null}
                      </div>
                      <p style={{ color: '#BE84A3', fontSize: '12px' }}>{u.email} · {tiempoDesde(u.ultimo_visto)}</p>
                    </div>
                    <button
                      onClick={() => cerrarSesion(u.id, u.nombre)}
                      disabled={cerrandoSesion === u.id || u.id === usuario?.id}
                      style={{ background: u.id === usuario?.id ? '#F3F4F6' : 'rgba(248,113,113,0.1)', border: `1px solid ${u.id === usuario?.id ? '#E5E7EB' : 'rgba(248,113,113,0.3)'}`, color: u.id === usuario?.id ? '#9CA3AF' : '#F87171', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: u.id === usuario?.id ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                    >
                      {cerrandoSesion === u.id ? '...' : u.id === usuario?.id ? 'Tú' : '⏏ Cerrar sesión'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── TAB: SESIONES ─────────────────────────────────── */}
        {tab === 'sesiones' && (
          <>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                style={{ ...inputStyle, maxWidth: '340px', marginBottom: 0 }}
                placeholder="🔍 Filtrar por nombre, email o IP..."
                value={filtroBusq}
                onChange={e => setFiltroBusq(e.target.value)}
              />
              <button onClick={cargarSesiones} style={{ background: '#FCE7F3', border: '1px solid #FBCFE8', color: '#EC4899', padding: '10px 16px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                ↻ Actualizar
              </button>
              <span style={{ color: '#BE84A3', fontSize: '12px' }}>{sesionesFiltered.length} sesiones</span>
            </div>

            {cargandoSes ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#BE84A3' }}>Cargando...</div>
            ) : sesionesFiltered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px', color: '#BE84A3', background: '#FFFFFF', borderRadius: '14px', border: '1px solid #FBCFE8' }}>No hay sesiones registradas aún</div>
            ) : esMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {sesionesFiltered.map(s => (
                  <div key={s.id} style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '12px', padding: '14px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <div>
                        <p style={{ color: '#581C47', fontWeight: 600, fontSize: '14px' }}>{s.nombre}</p>
                        <p style={{ color: '#BE84A3', fontSize: '11px' }}>{s.email}</p>
                      </div>
                      <span style={{ background: '#FCE7F3', color: '#EC4899', borderRadius: '6px', padding: '2px 8px', fontSize: '10px', fontWeight: 700, alignSelf: 'flex-start' }}>{s.rol?.toUpperCase()}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <span style={{ color: '#BE84A3', fontSize: '11px' }}>📍 {s.ip || '—'}</span>
                      <span style={{ color: '#BE84A3', fontSize: '11px' }}>🕐 {fmt(s.fecha)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="table-scroll">
                <table style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #FBCFE8', background: '#FFF0F5' }}>
                      {['Usuario', 'Rol', 'IP', 'Dispositivo', 'Fecha'].map(h => (
                        <th key={h} style={{ padding: '11px 16px', color: '#BE84A3', fontSize: '11px', textAlign: 'left', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sesionesFiltered.map((s, i) => (
                      <tr key={s.id} style={{ borderBottom: i < sesionesFiltered.length - 1 ? '1px solid #FCE7F3' : 'none' }}>
                        <td style={{ padding: '11px 16px' }}>
                          <p style={{ color: '#581C47', fontSize: '13px', fontWeight: 500 }}>{s.nombre}</p>
                          <p style={{ color: '#BE84A3', fontSize: '11px' }}>{s.email}</p>
                        </td>
                        <td style={{ padding: '11px 16px' }}>
                          <span style={{ background: '#FCE7F3', color: '#EC4899', borderRadius: '6px', padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>{s.rol?.toUpperCase()}</span>
                        </td>
                        <td style={{ padding: '11px 16px', color: '#581C47', fontSize: '12px', fontFamily: 'monospace' }}>{s.ip || '—'}</td>
                        <td style={{ padding: '11px 16px', color: '#BE84A3', fontSize: '11px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.user_agent ? s.user_agent.substring(0, 50) + (s.user_agent.length > 50 ? '…' : '') : '—'}
                        </td>
                        <td style={{ padding: '11px 16px', color: '#BE84A3', fontSize: '12px', whiteSpace: 'nowrap' }}>{fmt(s.fecha)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ── TAB: INACTIVIDAD ──────────────────────────────── */}
        {tab === 'inactividad' && (
          <>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '10px', padding: '8px 16px' }}>
                <span style={{ color: '#BE84A3', fontSize: '13px' }}>Alumnos sin login en más de</span>
                <input
                  type="number" min="1" max="365"
                  style={{ width: '60px', padding: '4px 8px', borderRadius: '6px', background: '#FFF0F5', border: '1px solid #FBCFE8', color: '#581C47', fontSize: '14px', fontWeight: 600, textAlign: 'center' }}
                  value={dias}
                  onChange={e => setDias(e.target.value)}
                />
                <span style={{ color: '#BE84A3', fontSize: '13px' }}>días</span>
              </div>
              <button onClick={cargarInactivos} style={btnGrad}>Buscar</button>
            </div>

            {cargandoInact ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#BE84A3' }}>Buscando...</div>
            ) : inactivos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px', color: '#BE84A3', background: '#FFFFFF', borderRadius: '14px', border: '1px solid #FBCFE8' }}>
                No hay alumnos inactivos en los últimos {dias} días 🎉
              </div>
            ) : (
              <>
                <p style={{ color: '#F59E0B', fontWeight: 600, fontSize: '14px', marginBottom: '14px' }}>
                  ⚠️ {inactivos.length} alumno(s) sin actividad en más de {dias} días
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {inactivos.map(u => (
                    <div key={u.id} style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '12px', padding: esMobile ? '14px 16px' : '14px 20px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ color: '#581C47', fontWeight: 600, fontSize: '14px', marginBottom: '2px' }}>{u.nombre}</p>
                        <p style={{ color: '#BE84A3', fontSize: '12px' }}>{u.email}</p>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ background: u.suscripcion_activa ? '#FCE7F3' : '#FFF0F5', color: u.suscripcion_activa ? '#EC4899' : '#BE84A3', border: '1px solid #FBCFE8', borderRadius: '6px', padding: '2px 10px', fontSize: '11px', fontWeight: 700 }}>
                          {u.suscripcion_activa ? '💳 Pago activo' : '— Sin pago'}
                        </span>
                        <span style={{ color: u.ultima_sesion ? '#F59E0B' : '#F87171', fontSize: '12px', fontWeight: 600 }}>
                          🕐 {tiempoDesde(u.ultima_sesion)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* ── TAB: IPs BLOQUEADAS ───────────────────────────── */}
        {tab === 'ips' && (
          <>
            <form onSubmit={bloquearIp} style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '14px', padding: esMobile ? '16px' : '20px 24px', marginBottom: '20px', boxShadow: '0 2px 10px rgba(236,72,153,0.06)' }}>
              <h3 style={{ color: '#EC4899', fontSize: '14px', marginBottom: '14px' }}>Bloquear nueva IP</h3>
              {errorIp && <div style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: '8px', padding: '8px 12px', color: '#F87171', fontSize: '13px', marginBottom: '12px' }}>{errorIp}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: esMobile ? '1fr' : '160px 1fr auto', gap: '10px', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ color: '#BE84A3', fontSize: '12px', display: 'block', marginBottom: '5px' }}>Dirección IP *</label>
                  <input style={{ ...inputStyle, fontFamily: 'monospace' }} placeholder="192.168.1.1" value={ipForm.ip} onChange={e => setIpForm({ ...ipForm, ip: e.target.value })} required />
                </div>
                <div>
                  <label style={{ color: '#BE84A3', fontSize: '12px', display: 'block', marginBottom: '5px' }}>Motivo (opcional)</label>
                  <input style={inputStyle} placeholder="Ej: Intentos de acceso sospechosos" value={ipForm.motivo} onChange={e => setIpForm({ ...ipForm, motivo: e.target.value })} />
                </div>
                <button type="submit" disabled={guardandoIp} style={{ ...btnGrad, whiteSpace: 'nowrap', opacity: guardandoIp ? 0.6 : 1 }}>🚫 Bloquear</button>
              </div>
            </form>

            {ips.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px', color: '#BE84A3', background: '#FFFFFF', borderRadius: '14px', border: '1px solid #FBCFE8' }}>No hay IPs bloqueadas</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {ips.map(ip => (
                  <div key={ip.id} style={{ background: '#FFFFFF', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '12px', padding: esMobile ? '14px 16px' : '14px 20px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
                        <code style={{ background: 'rgba(248,113,113,0.08)', color: '#F87171', padding: '3px 10px', borderRadius: '6px', fontSize: '14px', fontWeight: 700 }}>{ip.ip}</code>
                        <span style={{ background: 'rgba(248,113,113,0.1)', color: '#F87171', borderRadius: '6px', padding: '2px 8px', fontSize: '10px', fontWeight: 700 }}>BLOQUEADA</span>
                      </div>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {ip.motivo && <span style={{ color: '#BE84A3', fontSize: '12px' }}>📝 {ip.motivo}</span>}
                        {ip.bloqueado_por_nombre && <span style={{ color: '#BE84A3', fontSize: '12px' }}>👤 {ip.bloqueado_por_nombre}</span>}
                        <span style={{ color: '#BE84A3', fontSize: '12px' }}>📅 {new Date(ip.fecha).toLocaleDateString('es-ES')}</span>
                      </div>
                    </div>
                    <button onClick={() => desbloquearIp(ip.id)} style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', color: '#065F46', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                      ✓ Desbloquear
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
